import { apiClient } from '@/shared/lib/axios';
import type {
  CreateRezeptSheetPayload,
  UpdateRezeptSheetPayload,
  RezeptSheet,
  RezeptSheetListQuery,
  RezeptSheetListResponse,
} from '@/types/admin-rezept-sheet';

type ApiEnvelope<T = unknown> = { success?: boolean; message?: string; data: T };

/**
 * Fetch paginated list of rezept sheets for admin.
 */
export const getRezeptSheetList = async (
  siteType: string,
  query?: RezeptSheetListQuery
): Promise<RezeptSheetListResponse> => {
  const response = await apiClient.get<ApiEnvelope<RezeptSheetListResponse>>(
    `/admin/${siteType}/rezept-sheets`,
    { params: query }
  );
  return response.data.data;
};

/**
 * Create a new rezept sheet.
 */
export const createRezeptSheet = async (
  siteType: string,
  data: CreateRezeptSheetPayload
): Promise<RezeptSheet> => {
  const response = await apiClient.post<ApiEnvelope<RezeptSheet>>(
    `/admin/${siteType}/rezept-sheets`,
    data
  );
  return response.data.data;
};

/**
 * Fetch a single rezept sheet by ID.
 */
export const getRezeptSheetDetail = async (
  siteType: string,
  id: number
): Promise<RezeptSheet> => {
  const response = await apiClient.get<ApiEnvelope<RezeptSheet>>(
    `/admin/${siteType}/rezept-sheets/${id}`
  );
  return response.data.data;
};

/**
 * Update a rezept sheet.
 */
export const updateRezeptSheet = async (
  siteType: string,
  id: number,
  data: UpdateRezeptSheetPayload
): Promise<RezeptSheet> => {
  const response = await apiClient.put<ApiEnvelope<RezeptSheet>>(
    `/admin/${siteType}/rezept-sheets/${id}`,
    data
  );
  return response.data.data;
};

/**
 * Delete a rezept sheet.
 */
export const deleteRezeptSheet = async (
  siteType: string,
  id: number
): Promise<void> => {
  await apiClient.delete(`/admin/${siteType}/rezept-sheets/${id}`);
};

/**
 * Download rezept sheets as CSV (blob).
 */
export const downloadRezeptSheetsCsv = async (
  siteType: string,
  query?: RezeptSheetListQuery
): Promise<void> => {
  const response = await apiClient.get(`/admin/${siteType}/rezept-sheets/csv`, {
    params: query,
    responseType: 'blob',
  });

  const blob = response.data;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `rezept_sheet${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
